/**
 * WidgetPickerModal — paleta „Dodaj kafelek" (tryb edycji).
 *
 * Trzy sekcje wbudowane + osobna sekcja widżetów z kreatora:
 *  1. Statystyki — karty i kompaktowe kafelki KPI (przychód, dokumenty, czas akceptacji, …),
 *  2. Wykresy — gotowe presety (obszarowy z porównaniem, słupkowy, liniowy, kołowy, „Nowe vs
 *     Zakończone", wydajność procesów),
 *  3. Listy i raporty — decyzje, aktywność, transakcje, powiadomienia, ostatnie rekordy, profil,
 *     szybkie akcje, notatka, odliczanie.
 * Klik → `onPick(type, undefined, configPatch)`; kontener dokłada instancję z domyślnym configiem
 * nadpisanym presetem.
 *  4. Widżety danych (kreator) — lista zapisanych definicji (`GET /widget-definitions`, bramka
 *     `crm_core.widgets.manage`). Klik → `onPick('custom', definition)` — kontener składa kafel
 *     z `config.definitionId`. Sekcja widoczna tylko dla usera z uprawnieniem zarządzania
 *     (inaczej lista i tak zwróciłaby 403); zwykły user widzi tylko typy wbudowane.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useWidgetDefinitions } from '@/hooks/useWidgetDefinitions';
import { resolveIcon } from '@/lib/icon-map';
import type { WidgetDefinitionDTO } from '@/lib/widgets/types';

import type { ChartVariant, DashboardEntity, KpiMetric, KpiVariant, WidgetType } from './types';
import { WIDGET_REGISTRY } from './widget-registry';

interface WidgetPickerModalProps {
  open: boolean;
  onClose: () => void;
  onPick: (
    type: WidgetType,
    definition?: WidgetDefinitionDTO,
    configPatch?: Record<string, unknown>,
  ) => void;
}

/** Jeden gotowy wybór w palecie: typ kafelka + nadpisanie configu + opis do wyświetlenia. */
interface Preset {
  key: string;
  type: WidgetType;
  config?: Record<string, unknown>;
  icon: string;
  title: string;
  description: string;
}

function usePresets() {
  const { t } = useTranslation();
  const metric = (m: string) => t(`tenant.dashboard.metrics.${m}`);
  const variant = (v: ChartVariant | 'pie') => t(`tenant.dashboard.chartVariant.${v}`);
  const desc = (type: WidgetType) => t(`tenant.dashboard.picker.desc.${type}`);

  const kpi = (m: KpiMetric, icon: string, v: KpiVariant = 'card'): Preset => ({
    key: `kpi-${m}-${v}`,
    type: 'kpi',
    config: { metric: m, variant: v, title: metric(m) },
    icon,
    title: metric(m),
    description: t(`tenant.dashboard.config.kpiVariant.${v}`),
  });

  const chart = (
    entity: DashboardEntity,
    v: ChartVariant,
    extra: Record<string, unknown> = {},
  ): Preset => ({
    key: `chart-${entity}-${v}-${Object.keys(extra).join('')}`,
    type: 'chart',
    config: { entity, variant: v, title: metric(entity), ...extra },
    icon: 'LayoutDashboard',
    title: metric(entity),
    description:
      variant(v) +
      (extra.measure === 'amount' ? ` · ${t('tenant.dashboard.config.measure.amount')}` : '') +
      (extra.compare ? ` · ${t('tenant.dashboard.config.compareLabel')}` : ''),
  });

  const breakdown = (entity: DashboardEntity, dimension: 'company' | 'status'): Preset => ({
    key: `breakdown-${entity}-${dimension}`,
    type: 'breakdown',
    config: {
      metric: entity,
      dimension,
      title: t(
        dimension === 'status'
          ? 'tenant.dashboard.widgets.breakdown.statusTitleFor'
          : 'tenant.dashboard.widgets.breakdown.titleFor',
        { name: metric(entity) },
      ),
    },
    icon: 'PieChart',
    title: metric(entity),
    description: `${variant('pie')} · ${t(`tenant.dashboard.config.dimension.${dimension}`)}`,
  });

  const plain = (type: WidgetType): Preset => ({
    key: `type-${type}`,
    type,
    icon: WIDGET_REGISTRY[type].icon,
    title: t(WIDGET_REGISTRY[type].labelKey),
    description: desc(type),
  });

  const stats: Preset[] = [
    kpi('sales_revenue', 'Wallet'),
    kpi('documents_total', 'FileText'),
    kpi('avg_approval_hours', 'Clock'),
    kpi('active_users', 'Users'),
    kpi('contractors', 'Contact'),
    kpi('employees', 'Users'),
    kpi('sales_revenue', 'Wallet', 'tile'),
    kpi('documents_total', 'FileText', 'tile'),
  ];

  const charts: Preset[] = [
    chart('sale_invoices', 'area', { measure: 'amount', compare: true }),
    chart('purchase_invoices', 'bar', { measure: 'amount' }),
    chart('contractors', 'area', { compare: true }),
    chart('employees', 'bar'),
    chart('products', 'line'),
    breakdown('employees', 'company'),
    breakdown('contractors', 'company'),
    breakdown('purchase_invoices', 'status'),
    plain('flow'),
    plain('process_performance'),
  ];

  const lists: Preset[] = [
    plain('pending_approvals'),
    plain('activity_feed'),
    plain('transactions'),
    plain('alerts'),
    plain('recent_records'),
    plain('profile'),
    plain('quick_actions'),
    plain('note'),
    plain('countdown'),
  ];

  return { stats, charts, lists };
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">{title}</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

export function WidgetPickerModal({ open, onClose, onPick }: WidgetPickerModalProps) {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const { stats, charts, lists } = usePresets();

  const canManageWidgets =
    me?.user.isSuperUser === true || (me?.permissions.includes('crm_core.widgets.manage') ?? false);

  // Definicje pobieramy tylko gdy picker otwarty i user może je listować (inaczej 403).
  const { data: definitions, isLoading: definitionsLoading } = useWidgetDefinitions(false, {
    enabled: open && canManageWidgets,
  });
  const activeDefinitions = (definitions ?? []).filter((d) => d.isActive);

  function renderPreset(p: Preset) {
    const IconCmp = resolveIcon(p.icon);
    return (
      <Button
        key={p.key}
        variant="secondary"
        onClick={() => {
          onPick(p.type, undefined, p.config);
          onClose();
        }}
        className="h-auto w-full items-start justify-start gap-3 p-3 text-left"
      >
        <span className="mt-0.5 rounded-lg bg-bg-tertiary p-2 text-text-tertiary">
          <IconCmp className="h-5 w-5" aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-text-primary">{p.title}</span>
          <span className="mt-0.5 block text-xs text-text-tertiary">{p.description}</span>
        </span>
      </Button>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={t('tenant.dashboard.picker.title')} size="lg">
      <div className="space-y-5">
        <Section title={t('tenant.dashboard.picker.statsSection')}>
          {stats.map(renderPreset)}
        </Section>
        <Section title={t('tenant.dashboard.picker.chartsSection')}>
          {charts.map(renderPreset)}
        </Section>
        <Section title={t('tenant.dashboard.picker.listsSection')}>
          {lists.map(renderPreset)}
        </Section>

        {canManageWidgets && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
              {t('tenant.dashboard.picker.customSection')}
            </h3>
            {definitionsLoading ? (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            ) : activeDefinitions.length === 0 ? (
              <p className="rounded-lg bg-bg-secondary px-3 py-4 text-center text-xs text-text-tertiary">
                {t('tenant.dashboard.picker.customEmpty')}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {activeDefinitions.map((def) => {
                  const IconCmp = resolveIcon(def.icon ?? 'LayoutDashboard');
                  return (
                    <Button
                      key={def.id}
                      variant="secondary"
                      onClick={() => {
                        onPick('custom', def);
                        onClose();
                      }}
                      className="h-auto w-full items-start justify-start gap-3 p-3 text-left"
                    >
                      <span className="mt-0.5 rounded-lg bg-bg-tertiary p-2 text-text-tertiary">
                        <IconCmp className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-text-primary">
                          {def.name}
                        </span>
                        <span className="mt-0.5 block text-xs text-text-tertiary">
                          {t(`tenant.widgets.visualType.${def.visualType}`)}
                        </span>
                      </span>
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
